import React, { useState } from 'react';
import { Box, Container, Typography, Grid } from '@mui/material';
import Img from '../../components/Img';
import { motion } from 'framer-motion';
import { Play, ArrowRight } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import VideoModal from '../../components/VideoModal';
import SectionHeading from '../../components/SectionHeading';
import { colors } from '../../theme';
import { ease } from '../../utils/animations';
import { track } from '../../utils/analytics';

const FILM_SRC = '/media/minneai-story.mp4';
const FILM_POSTER = '/media/story-poster.webp';

const FilmSection = () => {
    const [open, setOpen] = useState(false);

    return (
        <Box
            id="story"
            component="section"
            sx={{
                backgroundColor: '#0b0b0b',
                color: '#fff',
                py: { xs: 9, md: 14 },
                scrollMarginTop: { xs: '56px', md: '64px' },
            }}
        >
            <Container maxWidth="lg">
                <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
                    {/* Left-aligned heading -- the first on the page, which is
                        what makes this read as a different kind of moment. */}
                    <Grid size={{ xs: 12, md: 5 }}>
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.4 }}
                            transition={{ duration: 0.7, ease }}
                        >
                            <SectionHeading
                                eyebrow="Inside minneAI"
                                title="How we work."
                                tone="light"
                                sx={{ mb: 3 }}
                            />
                            <Typography
                                sx={{
                                    color: 'rgba(255,255,255,0.72)',
                                    fontSize: '1.05rem',
                                    lineHeight: 1.75,
                                    maxWidth: 460,
                                }}
                            >
                                One client, one build. WalkPort is an FDA-registered walk
                                station — we wrote the firmware that reads the machine's
                                sensors and the app that counts every step, working
                                alongside the client the whole way. Two and a half minutes
                                of what that looks like.
                            </Typography>
                            <Box
                                component={RouterLink}
                                to="/work/walkport"
                                onClick={() => track('work_open', { project: 'walkport', source: 'film' })}
                                sx={{
                                    mt: 3, display: 'inline-flex', alignItems: 'center', gap: 0.75, py: 0.5,
                                    color: '#fff', fontWeight: 600, fontSize: '0.98rem', textDecoration: 'none',
                                    '&:hover': { color: colors.green },
                                    '&:focus-visible': { outline: `2px solid ${colors.green}`, outlineOffset: 3 },
                                }}
                            >
                                See the WalkPort project <ArrowRight size={16} />
                            </Box>
                        </motion.div>
                    </Grid>

                    {/* Poster -> modal. Nothing downloads until it's clicked. */}
                    <Grid size={{ xs: 12, md: 7 }}>
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.7, delay: 0.12, ease }}
                        >
                            <Box
                                role="button"
                                tabIndex={0}
                                aria-label="Play the video"
                                onClick={() => {
                                    track('film_play');
                                    setOpen(true);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        track('film_play');
                                        setOpen(true);
                                    }
                                }}
                                sx={{
                                    position: 'relative',
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    lineHeight: 0,
                                    boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
                                    '&:focus-visible': {
                                        outline: `2px solid ${colors.green}`,
                                        outlineOffset: 3,
                                    },
                                    '&:hover .film-scrim': { backgroundColor: 'rgba(0,0,0,0.20)' },
                                    // Must repeat the centring translate: a bare scale()
                                    // replaces the whole transform and the button jumps.
                                    '&:hover .film-play': {
                                        transform: 'translate(-50%, -50%) scale(1.06)',
                                        backgroundColor: colors.green,
                                    },
                                }}
                            >
                                <Img
                                    src={FILM_POSTER}
                                    width={800}
                                    height={333}
                                    alt="Paul Tran with a client on the Mississippi riverbank"
                                    sx={{ width: '100%', height: 'auto', display: 'block' }}
                                />
                                <Box
                                    className="film-scrim"
                                    sx={{
                                        position: 'absolute', inset: 0,
                                        backgroundColor: 'rgba(0,0,0,0.32)',
                                        transition: 'background-color 0.3s ease',
                                    }}
                                />
                                <Box
                                    className="film-play"
                                    sx={{
                                        position: 'absolute',
                                        top: '50%', left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        width: { xs: 56, md: 78 }, height: { xs: 56, md: 78 },
                                        '& svg': { width: { xs: 22, md: 30 }, height: { xs: 22, md: 30 } },
                                        borderRadius: '50%',
                                        backgroundColor: 'rgba(255,255,255,0.16)',
                                        border: '1.5px solid rgba(255,255,255,0.75)',
                                        backdropFilter: 'blur(6px)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'transform 0.3s ease, background-color 0.3s ease',
                                    }}
                                >
                                    <Play size={30} color="#fff" fill="#fff" style={{ marginLeft: 4 }} />
                                </Box>
                            </Box>
                            <Typography
                                sx={{
                                    mt: 2,
                                    fontSize: '0.85rem',
                                    color: 'rgba(255,255,255,0.82)',
                                    letterSpacing: '0.02em',
                                }}
                            >
                                Watch · 2 min 25 sec
                            </Typography>
                        </motion.div>
                    </Grid>
                </Grid>
            </Container>

            <VideoModal
                open={open}
                onClose={() => setOpen(false)}
                src={FILM_SRC}
                poster={FILM_POSTER}
            />
        </Box>
    );
};

export default FilmSection;
